import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CreateGalleryPresetBody,
  GalleryDto,
  GalleryPresetDto,
  StandardGalleryPresetDto,
  UpdateGalleryPresetBody,
} from '@nusign/picflow-backend-types';
import { firstValueFrom } from 'rxjs';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { galleryToPreset } from '../../../lib/new-gallery';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { GalleryPresetService } from '../../../services/gallery-preset.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { GallerySettingsMenuComponent } from '../../menus/gallery-settings-menu/gallery-settings-menu.component';

@Component({
  selector: 'app-save-gallery-preset-dialog',
  templateUrl: './save-gallery-preset-dialog.component.html',
  styleUrls: ['./save-gallery-preset-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    ButtonDirective,
    LoadingIndicatorComponent,
    AutofocusDirective,
  ],
})
export class SaveGalleryPresetDialogComponent implements OnGetData {
  private check = inject(ChangeDetectorRef);
  private galleryPresetService = inject(GalleryPresetService);
  private dialog = inject(DialogService);
  dialogName = 'save-gallery-preset';
  close: (...args) => void;
  gallery: GalleryDto;
  customPresets: (GalleryPresetDto | StandardGalleryPresetDto)[];
  settingsMode: string;
  mode: 'new' | 'override' = 'new';
  loading = false;
  newPresetForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
  });
  overridePresetForm = new FormGroup({
    id: new FormControl('', [Validators.required]),
  });

  dialogOptions(): DialogOptions {
    return { size: 480, spacingX: 60, spacingY: 40 };
  }

  async onGetData(data: { gallery: GalleryDto; settingsMode: string }, close: (...args) => void) {
    this.close = close;
    this.gallery = data.gallery;
    this.settingsMode = data.settingsMode;
    this.customPresets = await firstValueFrom(this.galleryPresetService.getCustomGalleryPresets());
    if (this.customPresets.length > 0) {
      this.overridePresetForm.patchValue({ id: this.customPresets[0].id });
    }
    this.check.detectChanges();
  }

  async savePreset() {
    if (this.mode === 'new') {
      if (!this.newPresetForm.valid) return;
      const params: CreateGalleryPresetBody = {
        name: this.newPresetForm.value.name,
        ...galleryToPreset(this.gallery),
      };
      this.loading = true;
      await firstValueFrom(this.galleryPresetService.createPreset(params));
    } else {
      if (!this.overridePresetForm.valid) return;
      const params: UpdateGalleryPresetBody = galleryToPreset(this.gallery);
      this.loading = true;
      await firstValueFrom(this.galleryPresetService.updatePreset(this.overridePresetForm.value.id, params));
    }
    this.dialog.open(GallerySettingsMenuComponent, { gallery: this.gallery });
  }
}
